/* eslint-disable no-underscore-dangle */
import React, { useEffect } from 'react';
import { t } from '@lingui/macro';
import { z } from 'zod';

import { Text } from '@rover/kibble/core';
import { Form, useKibbleForm } from '@rover/kibble/forms';
import useI18nContext from '@rover/react-lib/src/context/useI18nContext';
import { HomeAddressUpdate } from '@rover/rsdk/src/apiClient/latest';

import Container from './Container';
import KibbleAddressFields from './KibbleAddressFields';
import SaveButton from './SaveButton';

export interface KibbleAddressFormProps {
  onSubmit: (data: HomeAddressUpdate) => void;
  defaultValues?: Partial<HomeAddressUpdate>;
  disableCountry?: boolean;
  submissionErrors?: Record<string, string[]>;
  isLoading?: boolean;
}

const KibbleAddressForm: React.FC<KibbleAddressFormProps> = ({
  onSubmit,
  defaultValues,
  disableCountry,
  submissionErrors,
  isLoading,
}) => {
  const { locale } = useI18nContext();

  const addressSchema = z
    .object({
      addressLine1: z.string().min(1, t`Please enter a valid address`),
      addressLine2: z.string().optional(),
      city: z.string().min(1, t`Please enter a valid city`),
      state: z.string().optional(),
      zip: z.string().min(1, t`Please enter a valid ZIP code`),
      countryCode: z.string().min(1, t`Please enter a valid country`),
      countryCheckboxes: z.array(z.string()).optional(),
    })
    .superRefine((data, ctx) => {
      // Check if state is required for US
      if (data.countryCode === 'US' && (!data.state || data.state.trim() === '')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t`Please enter a valid state`,
          path: ['state'],
        });
      }

      // Check if TOS is required when country changes
      const countryChanged = data.countryCode !== defaultValues?.countryCode;
      if (countryChanged && !data.countryCheckboxes?.includes('agreeTOS')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t`You must accept the Rover Terms of Service to continue`,
          path: ['countryCheckboxes'],
        });
      }
    });

  const {
    configureField,
    formProps,
    submitButtonProps,
    setValue,
    getValues,
    setError,
    clearErrors,
  } = useKibbleForm({
    schema: addressSchema,
    onValidSubmit: (data) => {
      const submissionData: HomeAddressUpdate = {
        ...data,
        state: data.state || '',
        tos: true,
        emailOptIn: data.countryCheckboxes?.includes('emailOptIn') || false,
      };
      onSubmit(submissionData);
    },
    defaultValues: {
      ...defaultValues,
      countryCheckboxes: [],
    },
  });

  useEffect(() => {
    if (submissionErrors) {
      Object.entries(submissionErrors).forEach(([key, value]) => {
        // @ts-expect-error
        setError(key, { message: value[0] });
      });
    }
  }, [submissionErrors, setError]);

  return (
    <Form {...formProps} hideRequiredFieldWarning>
      <Container>
        {submissionErrors?._All__ && (
          <Text size="100" textColor="error" mb="2x">
            {submissionErrors._All__}
          </Text>
        )}
        <KibbleAddressFields
          addressField={configureField('addressLine1')}
          cityField={configureField('city')}
          stateField={configureField('state')}
          zipField={configureField('zip')}
          countryField={configureField('countryCode')}
          addressLine2Field={configureField('addressLine2')}
          countryCheckboxesField={configureField('countryCheckboxes')}
          showStateField={getValues('countryCode') === 'US'}
          locale={locale}
          setValue={setValue as (name: string, value: string) => void}
          clearErrors={clearErrors as (name?: string) => void}
          disableCountry={disableCountry}
          getValues={getValues}
        />
      </Container>
      <SaveButton {...submitButtonProps} loading={isLoading} label={t`Save`} />
    </Form>
  );
};

export default KibbleAddressForm;
